import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import BorderDateSelect from 'src/components/form-new/border-date-select';
import { formatDateForInput } from 'src/helpers/date.helpers';
import { useGetEtlDataStatusQuery } from 'src/redux/operations/operations.endpoints';
import { EtlStatusBadge } from './etl-status-badge';

export function DataStatusSection() {
  const [selectedDate, setSelectedDate] = useState(() => formatDateForInput(new Date()));
  const { data, isLoading, isFetching, isError, error, refetch } = useGetEtlDataStatusQuery(
    { date: selectedDate },
    { skip: !selectedDate },
  );

  const helperText = useMemo(() => {
    if (!data?.message) {
      return null;
    }
    return data.message;
  }, [data?.message]);

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
      aria-labelledby="etl-data-status-heading"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="etl-data-status-heading" className="text-base font-semibold text-gray-900">
            Data status by date
          </h2>
          <p className="text-sm text-gray-600">Select a date to view load status for that day.</p>
        </div>
        <button
          type="button"
          className="text-sm font-medium text-violet-700 hover:text-violet-900 disabled:opacity-50"
          onClick={() => void refetch()}
          disabled={isFetching || !selectedDate}
        >
          Refresh
        </button>
      </div>

      <div className="mb-4 max-w-xs">
        <BorderDateSelect
          label="Date"
          value={selectedDate}
          onChange={(event) => setSelectedDate(event.target.value)}
        />
      </div>

      {(isLoading || isFetching) && (
        <div className="flex items-center gap-2 text-sm text-gray-600" role="status">
          <Loader2 className="size-4 animate-spin" />
          Loading data status…
        </div>
      )}

      {isError && !isLoading && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
          <p className="font-medium">Could not load data status</p>
          <p className="mt-1 text-red-700">
            {(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
          </p>
        </div>
      )}

      {!isLoading && !isError && !isFetching && !data?.status && (
        <p className="text-sm text-gray-600">
          No data status returned for
          {' '}
          {selectedDate}
          .
        </p>
      )}

      {!isLoading && !isError && data?.status && (
        <div className="grid gap-4 sm:grid-cols-2">
          <EtlStatusBadge label="Status" value={data.status} />
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-600">Date</span>
            <span className="text-sm font-semibold text-gray-900">{data.date ?? selectedDate}</span>
          </div>
          {helperText ? (
            <p className="sm:col-span-2 text-sm text-gray-600">{helperText}</p>
          ) : null}
        </div>
      )}
    </section>
  );
}
